import React, { useState, useEffect } from 'react';
import { FiBarChart2 } from 'react-icons/fi';
import { getProjectStats } from '../services/api';
import { SkeletonCard, SkeletonProgressBar } from './SkeletonLoader';
import './StatusOverview.css';

const GAUGE_R = 38;
const GAUGE_C = 2 * Math.PI * GAUGE_R;

function CoverageGauge({ valueKm2, totalKm2, color, label, stage }) {
  const pct = totalKm2 > 0 ? Math.min(1, Number(valueKm2) / Number(totalKm2)) : 0;
  const dash = pct * GAUGE_C;

  return (
    <div className="coverage-gauge" data-stage={stage}>
      <div className="coverage-gauge-chart">
        <svg viewBox="0 0 100 100" className="coverage-gauge-svg" aria-hidden>
          <circle
            className="coverage-gauge-track"
            cx="50"
            cy="50"
            r={GAUGE_R}
            fill="none"
            strokeWidth="9"
          />
          <circle
            className="coverage-gauge-arc"
            cx="50"
            cy="50"
            r={GAUGE_R}
            fill="none"
            stroke={color}
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${GAUGE_C}`}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="coverage-gauge-center">
          <div className="coverage-gauge-inner">
            <span className="coverage-gauge-value" style={{ color }}>
              {valueKm2}
            </span>
            <span className="coverage-gauge-ratio">/ {totalKm2}</span>
            <span className="coverage-gauge-unit">km²</span>
          </div>
        </div>
      </div>
      <div className="coverage-gauge-label">{label}</div>
    </div>
  );
}

function StatusOverview({ projectId, selectedKmlLayerIds }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      fetchStats();
    }
  }, [projectId, selectedKmlLayerIds]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const opts = {};
      if (Array.isArray(selectedKmlLayerIds)) {
        opts.kmlLayerIds = selectedKmlLayerIds;
      }
      const data = await getProjectStats(projectId, opts);
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!projectId || loading) {
    return (
      <div className="status-overview">
        <div className="status-overview-header">
          <FiBarChart2 className="status-overview-icon" />
          <h2>Status Overview</h2>
        </div>
        <SkeletonCard />
        <SkeletonProgressBar />
        <SkeletonProgressBar />
        <SkeletonProgressBar />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const tierAreas = stats.tierAreasKm2 ?? {
    processing: 0,
    acquisition: 0,
    gcp: 0,
    notStarted: 0,
  };

  const defaultStageAreas = tierAreas;
  const gaugeAreas = stats.inclusiveStageAreasKm2 ?? defaultStageAreas;

  const totalArea = Number(stats.totalArea) || 0;

  return (
    <div className="status-overview">
      <div className="status-overview-header">
        <FiBarChart2 className="status-overview-icon" />
        <h2>Status Overview</h2>
      </div>

      <div className="stats-summary">
        <div className="stat-item">
          <div className="stat-row">
            <div className="stat-column">
              <div className="stat-label">Total Blocks</div>
              <div className="stat-value">{stats.totalBlocks}</div>
            </div>
            <div className="stat-column">
              <div className="stat-label">Total Size</div>
              <div className="stat-value">{stats.totalArea ? `${stats.totalArea} km²` : '0 km²'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tier-area-section">
        <div className="tier-area-heading">Area by highest stage (map)</div>
        <div className="tier-area-grid">
          <div className="tier-area-item" data-stage="processing">
            <span className="tier-area-label">Processing</span>
            <span className="tier-area-value">{tierAreas.processing} km²</span>
          </div>
          <div className="tier-area-item" data-stage="acquisition">
            <span className="tier-area-label">Acquisition</span>
            <span className="tier-area-value">{tierAreas.acquisition} km²</span>
          </div>
          <div className="tier-area-item" data-stage="gcp">
            <span className="tier-area-label">GCPs</span>
            <span className="tier-area-value">{tierAreas.gcp} km²</span>
          </div>
          <div className="tier-area-item" data-stage="not-started">
            <span className="tier-area-label">Not started</span>
            <span className="tier-area-value">{tierAreas.notStarted} km²</span>
          </div>
        </div>
      </div>

      <div className="progress-section">
        <div className="progress-item">
          <div className="progress-header">
            <span className="progress-label">GCPs Compilation</span>
            <span className="progress-percentage">{stats.gcpStatus.percentage}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${stats.gcpStatus.percentage}%`,
                background: `linear-gradient(90deg, #ffc107 0%, #f57f17 100%)`,
              }}
            >
              {stats.gcpStatus.percentage > 10 && `${stats.gcpStatus.percentage}%`}
            </div>
          </div>
          <div className="progress-detail">
            {stats.gcpStatus.completed} / {stats.totalBlocks} blocks completed
          </div>
        </div>

        <div className="progress-item">
          <div className="progress-header">
            <span className="progress-label">Drone Acquisition</span>
            <span className="progress-percentage">{stats.acquisitionStatus.percentage}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${stats.acquisitionStatus.percentage}%`,
                background: `linear-gradient(90deg, #2196f3 0%, #1976d2 100%)`,
              }}
            >
              {stats.acquisitionStatus.percentage > 10 && `${stats.acquisitionStatus.percentage}%`}
            </div>
          </div>
          <div className="progress-detail">
            {stats.acquisitionStatus.completed} / {stats.totalBlocks} blocks completed
          </div>
        </div>

        <div className="progress-item">
          <div className="progress-header">
            <span className="progress-label">Data Processing</span>
            <span className="progress-percentage">{stats.processingStatus.percentage}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${stats.processingStatus.percentage}%`,
                background: `linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)`,
              }}
            >
              {stats.processingStatus.percentage > 10 && `${stats.processingStatus.percentage}%`}
            </div>
          </div>
          <div className="progress-detail">
            {stats.processingStatus.completed} / {stats.totalBlocks} blocks completed
          </div>
        </div>
      </div>

      <div className="chart-section">
        <h3 className="coverage-section-title">Total Coverage</h3>
        <div className="coverage-gauges-grid">
          <CoverageGauge
            stage="processing"
            label="Processing"
            color="#1b5e20"
            valueKm2={gaugeAreas.processing}
            totalKm2={totalArea}
          />
          <CoverageGauge
            stage="acquisition"
            label="Acquisition"
            color="#1976d2"
            valueKm2={gaugeAreas.acquisition}
            totalKm2={totalArea}
          />
          <CoverageGauge
            stage="gcp"
            label="GCPs"
            color="#f57f17"
            valueKm2={gaugeAreas.gcp}
            totalKm2={totalArea}
          />
          <CoverageGauge
            stage="not-started"
            label="Not started"
            color="#c62828"
            valueKm2={gaugeAreas.notStarted}
            totalKm2={totalArea}
          />
        </div>
      </div>
    </div>
  );
}

export default StatusOverview;
